import { decodeTokenSub } from '@/client/tokenStorage';

const BASE_KEY = 'promo-popup-shown';

/**
 * Fallback scope when no account owns the tab. The pop-up only renders behind
 * auth (`useMyPopup` is gated on a session), so this is defensive rather than
 * a reachable product state — but it must not be a SHARED bucket, hence a
 * distinct literal rather than the bare base key.
 */
const ANONYMOUS_SCOPE = 'anonymous';

/**
 * TCX-917, carrying over TCX-836's rule — the pop-up is shown once per browser
 * session, not once per page load.
 *
 * `sessionStorage`, not `localStorage`: the rule is "once per visit", so
 * closing the tab has to start a fresh one. It survives reloads and
 * navigation within the tab, which is exactly the complaint TCX-836 was
 * raised for — leaving the platform through the TCX logo and coming back
 * made the pop-up reappear as if it were a new login.
 *
 * The key is scoped to the access token's `sub`, the only authoritative
 * identity on the front end. Role is NOT identity: two consultants share a
 * role, so a role-scoped key would let one account's visit suppress the
 * other's pop-up in a tab that switched accounts without a reload.
 *
 * Every accessor is wrapped: `sessionStorage` throws outright in some privacy
 * modes, and a pop-up is not worth a render crash. Both failure paths fall
 * back to SHOWING the pop-up — the behaviour before this rule existed — so a
 * storage failure can only ever cost an extra impression, never silence a
 * campaign entirely.
 */
export const promoPopupSeenKey = (): string => {
  try {
    const sub = decodeTokenSub(window.localStorage.getItem('accessToken'));
    return `${BASE_KEY}:${sub ?? ANONYMOUS_SCOPE}`;
  } catch {
    return `${BASE_KEY}:${ANONYMOUS_SCOPE}`;
  }
};

export const hasSeenPromoPopup = (key: string): boolean => {
  try {
    return window.sessionStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
};

/**
 * Called when the pop-up is DISPLAYED, not when it is dismissed.
 *
 * That is the rule as specified: one appearance per session, whether or not
 * the viewer engages with it. Marking on dismissal instead would mean a
 * record with no auto-dismiss timer, shown to someone who simply reloads
 * without clicking the X, reappears on every single load — which is the
 * behaviour being removed.
 */
export const markPromoPopupSeen = (key: string): void => {
  try {
    window.sessionStorage.setItem(key, 'true');
  } catch {
    // Nothing to do: the pop-up has already rendered, and the only cost is
    // that this session may show it again.
  }
};
